import type { HTMLAttributes } from "react";
import { UserRoles } from "@/types/auth";
import { SearchFilterParams } from "@/types/borrower";
import { createdParams } from "./createdFilter";

export type FilterParam = Exclude<keyof SearchFilterParams, "search">;

/** Where a filter's suggested values come from; "none" filters take free text. */
export type FilterOptionsSource =
  | "status"
  | "brokers"
  | "underwriters"
  | "states"
  | "created"
  | "organizations"
  | "none";

export interface FilterViewer {
  role?: UserRoles;
  organizationCount: number;
}

export interface SearchFilter {
  /** The literal typed in the bar and inserted on accept. */
  key: string;
  aliases: readonly string[];
  label: string;
  param: FilterParam;
  /** Absent when the filter cannot be negated. */
  excludeParam?: FilterParam;
  /** Accepts comma lists (OR). */
  multi: boolean;
  options: FilterOptionsSource;
  /** How typed text narrows the suggested values. */
  match: "prefix" | "contains";
  /** Keyboard hints for the filters sheet's text input. */
  textInput?: Pick<HTMLAttributes<HTMLInputElement>, "inputMode" | "autoCapitalize">;
  /** Whether the key is offered to this viewer; typed keys always work. */
  isOffered: (viewer: FilterViewer) => boolean;
  /** Turns the values into params when they are not plain `param=value` pairs. */
  toParams?: (values: string[]) => Partial<SearchFilterParams>;
  normalize?: (value: string) => string;
}

const everyone = () => true;

const textFilter = (
  key: string,
  label: string,
  param: FilterParam,
  textInput: SearchFilter["textInput"],
  aliases: readonly string[] = []
): SearchFilter => ({
  key,
  aliases,
  label,
  param,
  excludeParam: `exclude_${param}` as FilterParam,
  multi: true,
  options: "none",
  match: "contains",
  textInput,
  isOffered: everyone,
});

/** Every search filter, in the order the key list and the filters sheet show them. */
export const SEARCH_FILTERS: readonly SearchFilter[] = [
  {
    key: "status",
    aliases: [],
    label: "Status",
    param: "app_status",
    excludeParam: "exclude_app_status",
    multi: true,
    options: "status",
    match: "prefix",
    isOffered: everyone,
    normalize: (value) => value.toLowerCase(),
  },
  {
    key: "iso",
    aliases: [],
    label: "ISO",
    param: "broker_company_name",
    excludeParam: "exclude_broker_company_name",
    multi: true,
    options: "brokers",
    match: "contains",
    // Brokers only ever see their own applications.
    isOffered: ({ role }) => role !== "broker",
  },
  textFilter("oname", "Owner name", "owner_name", { autoCapitalize: "words" }),
  textFilter("company", "Company", "company_name", { autoCapitalize: "words" }, ["cname"]),
  textFilter("email", "Email", "email", { inputMode: "email", autoCapitalize: "none" }),
  textFilter("ein", "EIN", "ein", { autoCapitalize: "none" }),
  {
    key: "uw",
    aliases: [],
    label: "Underwriter",
    param: "underwriter_email",
    excludeParam: "exclude_underwriter_email",
    multi: true,
    options: "underwriters",
    match: "contains",
    // The underwriter list is empty for brokers and only the viewer for underwriters.
    isOffered: ({ role }) => role === "admin" || role === "admin_support",
  },
  {
    key: "state",
    aliases: [],
    label: "State",
    param: "state",
    excludeParam: "exclude_state",
    multi: true,
    options: "states",
    match: "prefix",
    isOffered: everyone,
  },
  {
    key: "created",
    aliases: [],
    label: "Created",
    param: "created_after",
    multi: false,
    options: "created",
    match: "prefix",
    isOffered: everyone,
    toParams: ([value]) => createdParams(value),
  },
  {
    key: "org",
    aliases: [],
    label: "Organization",
    param: "organization_name",
    excludeParam: "exclude_organization_name",
    multi: true,
    options: "organizations",
    match: "contains",
    isOffered: ({ organizationCount }) => organizationCount >= 2,
  },
];

const FILTER_BY_NAME = new Map(
  SEARCH_FILTERS.flatMap((filter) =>
    [filter.key, ...filter.aliases].map((name) => [name, filter] as const)
  )
);

/** The filter a typed key names, case-insensitively, or undefined. */
export function findFilter(name: string): SearchFilter | undefined {
  return FILTER_BY_NAME.get(name.toLowerCase());
}

export function offeredFilters(viewer: FilterViewer): SearchFilter[] {
  return SEARCH_FILTERS.filter((filter) => filter.isOffered(viewer));
}
